import React, { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ArrowUpDown } from "lucide-react";
import { Panel, COLORS, formatILS, ChartTooltipStyle } from "./ui";

const BAR_COLORS = [COLORS.indigo, COLORS.sky, COLORS.purple, COLORS.emerald, COLORS.amber, COLORS.rose];

const COLUMNS = [
  { key: "subject", label: "מקצוע" },
  { key: "requests", label: "בקשות" },
  { key: "lessonsHeld", label: "שיעורים" },
  { key: "uniqueStudents", label: "תלמידים" },
  { key: "activeTeachers", label: "מורים" },
  { key: "revenue", label: "הכנסה" },
  { key: "cancelRate", label: "% ביטולים" },
  { key: "avgRating", label: "דירוג" },
];

const TopSubjectsChart = ({ data, loading, error, onRetry, onSelectSubject }) => {
  const [sortKey, setSortKey] = useState("requests");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    if (!data) return [];
    const copy = [...data];
    copy.sort((a, b) => (sortDir === "desc" ? b[sortKey] - a[sortKey] : a[sortKey] - b[sortKey]));
    return copy;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <Panel
      eyebrow="מקצועות"
      title="מקצועות מובילים"
      subtitle="ביקוש, פעילות והכנסה לפי מקצוע"
      accent="amber"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!loading && !error && (!data || data.length === 0)}
    >
      <div className="h-48 sm:h-56 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sorted.slice(0, 6)} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={COLORS.grid} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="subject" width={90} tick={{ fontSize: 12, fill: "#374151" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={ChartTooltipStyle} formatter={(val) => [val, "בקשות"]} />
            <Bar dataKey="requests" radius={[0, 8, 8, 0]} barSize={16} onClick={(d) => onSelectSubject?.(d.subject)} cursor="pointer">
              {sorted.slice(0, 6).map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 border-b border-gray-100">
              {COLUMNS.map((c) => (
                <th key={c.key} className="text-right font-bold px-2 py-1.5 whitespace-nowrap">
                  <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-0.5 hover:text-gray-600">
                    {c.label} <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.subject}
                onClick={() => onSelectSubject?.(row.subject)}
                className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-2 py-1.5 font-semibold text-gray-800 whitespace-nowrap">{row.subject}</td>
                <td className="px-2 py-1.5 text-gray-600">{row.requests}</td>
                <td className="px-2 py-1.5 text-gray-600">{row.lessonsHeld}</td>
                <td className="px-2 py-1.5 text-gray-600">{row.uniqueStudents}</td>
                <td className="px-2 py-1.5 text-gray-600">{row.activeTeachers}</td>
                <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{formatILS(row.revenue)}</td>
                <td className="px-2 py-1.5 text-gray-600">{row.cancelRate}%</td>
                <td className="px-2 py-1.5 text-gray-600">{row.avgRating?.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export default TopSubjectsChart;
